import { useLazyQuery } from '@apollo/client'
import styled from '@emotion/styled'
import { UntitledIcon } from '@faceup/icons'
import { ulSearchSm } from '@faceup/icons/ulSearchSm'
import { Form } from '@faceup/ui'
import { Button, Empty, Input, Select, Spin, Typography } from '@faceup/ui-base'
import {
  ChannelType,
  Country,
  INSTITUTION_PASSWORD_MAX_LENGTH,
  INSTITUTION_PASSWORD_MIN_LENGTH,
  Institution,
} from '@faceup/utils'
import { useEffect, useState } from 'react'
import { graphql } from '../../__generated__'
import type { UserInstitution } from '../../Contexts/UserContext'
import useConfigForProject from '../../hooks/useConfigForProject'
import { useGetShortLinkByReportSourceId } from '../../hooks/useGetShortLinkByReportSourceId'
import { useMedia } from '../../mq'
import { sharedMessages } from '../../Shared/translations'
import { defineMessages, FormattedMessage, useIntl } from '../../TypedIntl'
import useAnalytics from '../../utils/analytics'
import useRegion from '../../utils/useRegion'

const { Text } = Typography

const query = {
  SearchPublicSchools: graphql(`
    query SearchPublicSchoolsQuery($name: GraphQLString, $country: Country) {
      searchPublicSchools(name: $name, showTestingSchool: true, country: $country) {
        country
        regionId
        institutionId
        address
        institutionName
        isRegistered
        isTesting
        hasActivePassword
        defaultFormId
        shortLink
      }
    }
  `),
}

const messages = defineMessages({
  schoolNameLabel: 'Shared.report.school',
  schoolNameError: 'FollowUp.SearchSchoolForm.schoolNameError',
  schoolNamePlaceholder: 'FollowUp.SearchSchoolForm.schoolNamePlaceholder',
  schoolNotFound: 'Shared.global.schoolNotFound',
  schoolPasswordLabel: 'FollowUp.SearchInstitution.passwordLabel',
  schoolPasswordPlaceholder: 'FollowUp.SearchInstitution.passwordPlaceholder',
  testingSchoolName: 'FollowUp.SearchInstitution.testingSchoolName',
  wrongAccessCodeMessage: 'FollowUp.CreateCompanyReportForm.wrongAccessCode',
})

type Props = {
  proceed: (props: UserInstitution) => void
}

const SearchSchoolForm = ({ proceed }: Props) => {
  const [loading, setLoading] = useState<boolean>(false)
  const { formatMessage } = useIntl()
  const isLgDown = useMedia('lgDown')
  const { discoverByReportPassword } = useRegion()
  const { track } = useAnalytics()
  const { getShortLinkByReportSourceId } = useGetShortLinkByReportSourceId()

  const [passwordInput, setPasswordInput] = useState({ value: '', error: false })
  const [nameInput, setNameInput] = useState({ value: '', error: false })
  const [selectedSchool, setSelectedSchool] = useState<(typeof filteredSchools)[0] | null>(null)

  const validateForm = () => {
    let ok = true

    if (!selectedSchool) {
      setNameInput({ ...nameInput, error: true })
      ok = false
    }

    if (
      selectedSchool?.hasActivePassword &&
      (passwordInput.value.length < INSTITUTION_PASSWORD_MIN_LENGTH ||
        passwordInput.value.length > INSTITUTION_PASSWORD_MAX_LENGTH)
    ) {
      setPasswordInput({ ...passwordInput, error: true })
      ok = false
    }

    return ok
  }

  const resetForm = () => {
    setNameInput({ value: '', error: false })
    setPasswordInput({ value: '', error: false })
    setSelectedSchool(null)
  }

  const { isSk } = useConfigForProject()

  const [searchSchools, { loading: schoolsLoading, data, refetch, called }] = useLazyQuery(
    query.SearchPublicSchools,
    {
      variables: {
        name: nameInput.value,
        country: isSk ? Country.Sk : undefined,
      },
    }
  )

  // biome-ignore lint/correctness/useExhaustiveDependencies: NameInput.value triggers search, not deps
  useEffect(() => {
    if (called) {
      refetch()
    } else {
      searchSchools()
    }
  }, [nameInput.value, called, refetch, searchSchools])

  const filteredSchools = data?.searchPublicSchools ?? []

  return (
    <>
      <Form.Item
        label={<FormattedMessage {...messages.schoolNameLabel} />}
        errorMessage={nameInput.error && <FormattedMessage {...messages.schoolNameError} />}
        formItemProps={{
          className: 'mb-[6px]',
          'data-test': 'search-school-form-item',
        }}
      >
        <Select
          filterOption={false}
          showSearch
          suffixIcon={null}
          loading={schoolsLoading}
          onClick={() =>
            track('search_institution_input_clicked', { institution_type: Institution.School })
          }
          onSelect={(value: string) => {
            setSelectedSchool(
              filteredSchools?.find(school => school?.institutionId === value) ?? null
            )
            setNameInput({ ...nameInput, error: false })
          }}
          onSearch={value => setNameInput({ value, error: false })}
          placeholder={
            <>
              <UntitledIcon icon={ulSearchSm} /> {formatMessage(messages.schoolNamePlaceholder)}
            </>
          }
          size='large'
          data-test='search-school-form-select-input'
          allowClear
          showAction={['focus']}
          popupRender={menu => <div data-test='search-school-form-select-menu'>{menu}</div>}
          labelRender={option => (option as unknown as { title: string }).title}
          options={filteredSchools.map(school => ({
            value: school.institutionId,
            title: school.isTesting ? (
              <FormattedMessage {...messages.testingSchoolName} />
            ) : (
              school.institutionName
            ),
            label: school.isTesting ? (
              <Text strong>
                <FormattedMessage {...messages.testingSchoolName} />
              </Text>
            ) : (
              <>
                {school.institutionName}
                <AddressText>{school.address}</AddressText>
              </>
            ),
          }))}
          notFoundContent={
            schoolsLoading ? (
              <Spin />
            ) : nameInput.value === '' ? null : (
              <Empty
                styles={{ image: { height: 60 } }}
                description={<FormattedMessage {...messages.schoolNotFound} />}
              />
            )
          }
        />
      </Form.Item>
      {selectedSchool?.hasActivePassword && (
        <Form.Item
          label={<FormattedMessage {...messages.schoolPasswordLabel} />}
          errorMessage={
            passwordInput.error && <FormattedMessage {...messages.wrongAccessCodeMessage} />
          }
          formItemProps={{
            'data-test': 'search-school-form-item-password',
          }}
        >
          <Input
            autoComplete='off'
            size='large'
            data-test='search-school-form-password'
            value={passwordInput.value}
            onChange={({ target: { value } }) => setPasswordInput({ value, error: false })}
          />
        </Form.Item>
      )}
      <Form.Item>
        <div style={{ display: 'flex', justifyContent: 'center', paddingTop: '16px' }}>
          <Button
            type='primary'
            htmlType='submit'
            block={isLgDown}
            loading={loading}
            data-test='search-school-form-continue'
            onClick={async () => {
              setLoading(true)

              if (!validateForm()) {
                setLoading(false)
                return
              }
              if (!selectedSchool?.hasActivePassword) {
                return proceed({
                  defaultFormId: selectedSchool?.defaultFormId ?? '',
                  institutionId: selectedSchool?.institutionId ?? '',
                  channelType: ChannelType.ReportingSystem,
                  type: Institution.School,
                  shortLink: selectedSchool?.shortLink ?? '',
                })
              }

              const discovery = await discoverByReportPassword(passwordInput.value.trim())
              if (discovery) {
                const shortLink = await getShortLinkByReportSourceId(
                  discovery.reportSourceId,
                  discovery.institutionId
                )
                if (shortLink) {
                  proceed({
                    defaultFormId: selectedSchool?.defaultFormId ?? '',
                    institutionId: selectedSchool?.institutionId ?? '',
                    password: passwordInput.value.trim(),
                    channelType: ChannelType.ReportingSystem,
                    type: discovery.type,
                    shortLink,
                  })

                  resetForm()
                } else {
                  setPasswordInput({ ...passwordInput, error: true })
                }
              } else {
                setPasswordInput({ ...passwordInput, error: true })
              }

              track('search_institution_continue_button_clicked', {
                institution_type: Institution.School,
              })

              setLoading(false)
            }}
          >
            <FormattedMessage {...sharedMessages.continue} />
          </Button>
        </div>
      </Form.Item>
    </>
  )
}

const AddressText = styled.div`
  font-size: 12px;
  color: rgba(80, 80, 80, 0.8);
`

export default SearchSchoolForm
